/*
 * ******************************************************************
 * Copyright (c) 2024-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";

/**
 * Provides actions related to string operations - copy, paste, upload file, download as file.
 *
 * Paste from clipboard:
 * ========================
 *   details:
 *   - get the clipboard content
 *   - [mode] - must be StringActionMode.PasteFromClipboard
 *
 *   settings:
 *   - (contentChange) - event emitter to hook to get the content of the clipboard
 *
 * Copy to clipboard:
 * ========================
 *   details:
 *   - copies the content into the clipboard and shows a signpost when clicked
 *   - [mode] - must be StringActionMode.CopyToClipboard
 *
 *   settings:
 *   - [content] - the content to be downloaded as file
 *
 * Download as file:
 * ========================
 *   details:
 *   - downloads the given content as a file
 *   - file type is always 'text/plain' - means .txt files
 *   - [mode] - must be StringActionMode.DownloadFile
 *
 *   settings:
 *   - [content] - the content to be downloaded as file
 *   - [downloadDefaultFileName] - name of the downloaded file
 *
 * Upload file:
 * ========================
 *   details:
 *   - file type is always 'text/plain' - means .txt files
 *   - shows error signpost if the file size is bigger than specified in [maxUploadSizeMB]
 *   - [mode] - must be StringActionMode.UploadFile
 *
 *   settings:
 *   - (contentChange) - event emitter to hook to get the content of the uploaded file
 *   - [maxUploadSizeMB] - maximum allowed file size in MB to be used for upload
 */
@Component({
    standalone: false,
               selector: "dr-string-action",
               templateUrl: "./dr-string-action.component.html",
               styleUrls: ["./dr-string-action.component.scss"]
           })
export class DrStringActionComponent implements OnInit {
    protected readonly StringActionMode = StringActionMode;

    private static readonly MEGABYTE_TO_BYTES_MULTIPLIER: number = 1024 * 1024;

    @Input()
    content: string;

    @Output()
    contentChange: EventEmitter<string> = new EventEmitter<string>();

    @Input()
    mode: StringActionMode = StringActionMode.CopyToClipboard;

    @Input()
    maxUploadSizeMB: number = 5;

    @Input()
    downloadDefaultFileName: string = "key";

    public label: string;
    public copied: boolean = false;
    public error: string = null;

    @ViewChild("fileInput") fileInputControl: ElementRef;

    constructor(public element: ElementRef,
                private cdr: ChangeDetectorRef) {
    }

    public ngOnInit(): void {
        this.label = this.getLabel();
    }

    public click(event: Event): void {
        event.stopPropagation();

        switch (this.mode) {
            case StringActionMode.CopyToClipboard:
                this.copyToClipboard();
                break;
            case StringActionMode.PasteFromClipboard:
                this.pasteFromClipboard();
                break;
            case StringActionMode.DownloadFile:
                this.downloadFile();
                break;
            case StringActionMode.UploadFile:
                this.uploadFile();
                break;
        }
        this.cdr.detectChanges();
    }

    private copyToClipboard(): void {
        navigator.clipboard.writeText(this.content)
            .then(() => this.copied = true);
    }

    private pasteFromClipboard(): void {
        navigator.clipboard.readText()
            .then(text => {
                if (text != null && text.length > 0) {
                    this.contentChange.emit(text);
                }
            });
    }

    private downloadFile(): void {
        const csvFile: Blob = new Blob([this.content], { type: "text/plain" });

        const a: HTMLAnchorElement = document.createElement("a");
        a.href = window.URL.createObjectURL(csvFile);
        a.download = this.downloadDefaultFileName + ".txt";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
    }

    private uploadFile(): void {
        this.error = null;

        if (this.fileInputControl != null) {
            this.fileInputControl.nativeElement.click();
        }
    }

    public handleFile(event: Event): void {
        const files: File[] = event.target["files"] as File[];

        if (files.length != 1) {
            this.error = "string.action.upload.error.moreThanOneFileSelected";
            return;
        }

        if (files[0].size > DrStringActionComponent.MEGABYTE_TO_BYTES_MULTIPLIER * this.maxUploadSizeMB) {
            this.error = "string.action.upload.error.fileTooBig";
            return;
        }

        files[0].text()
            .then(fileContent => this.contentChange.emit(fileContent));
    }

    public errorSignpostChanged(opened: boolean): void {
        if (!opened) {
            this.error = null;
        }
    }

    private getLabel(): string {
        switch (this.mode) {
            case StringActionMode.CopyToClipboard:
                return "string.action.copy";
            case StringActionMode.PasteFromClipboard:
                return "string.action.paste";
            case StringActionMode.DownloadFile:
                return "string.action.download";
            case StringActionMode.UploadFile:
                return "string.action.upload";
        }
    }
}

export enum StringActionMode {
    CopyToClipboard,
    PasteFromClipboard,
    DownloadFile,
    UploadFile
}
